/// <amd-module name="VSSUI/Components/Table/ColumnSorting" />
import { announce } from "VSS/Core/Util/Accessibility";
import { cellFromEvent } from "VSSUI/List";
import * as Resources from "VSSUI/Resources.Widgets";
import { KeyCode, setFocusVisible } from "VSSUI/Util";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { ITable, ITableColumn, ITableProps, SortOrder } from "./Table.Props";

/**
 * The sortDelegate function is used to notify the caller when a change to the
 * sort order is being requested.
 *
 * @param columnIndex - The index of the column the request was made on.
 * @param proposedSortOrder - The proposed sort order is based on the default changes
 *  from the current sort order of the column. If the affected column is not sorted
 *  or is sorted descending, SortOrder.ascending is proposed. If the column is
 *  currently sorted ascending, SortOrder.descending is proposed.
 * @param event - This is the event that caused the sorting request.
 */
export type sortDelegate = (
    columnIndex: number,
    proposedSortOrder: SortOrder,
    event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
) => void;

/**
 * The ColumnSorting class is a behavior that can be used with the Table
 * component to provide column sorting. To use the ColumnSorting, create an
 * instance passing the sorting delegate to the constructor. Then supply
 * the created behavior to the table.
 */
export class ColumnSorting<T> implements IBehavior<ITableProps<T>, ITable<T>> {
    private onSort: sortDelegate;
    private props: Readonly<ITableProps<T>>;
    private eventDispatch?: IEventDispatch;

    constructor(onSort: sortDelegate) {
        this.onSort = onSort;
    }

    public initialize = (props: Readonly<ITableProps<T>>, table: ITable<T>, eventDispatch: IEventDispatch): void => {
        this.props = props;

        this.eventDispatch = eventDispatch;
        eventDispatch.addEventListener("click", this.onClick);
        eventDispatch.addEventListener("keydown", this.onKeyDown);
    };

    public componentDidMount = (props: Readonly<ITableProps<T>>) => {
        this.props = props;
    };

    public componentDidUpdate = (props: Readonly<ITableProps<T>>) => {
        this.props = props;
    };

    public componentWillUnmount = () => {
        if (this.eventDispatch) {
            this.eventDispatch.removeEventListener("click", this.onClick as any);
            this.eventDispatch.removeEventListener("keydown", this.onKeyDown as any);
        }
    };

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            this.processSortEvent(event, true);
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                this.processSortEvent(event);
            }
        }
    };

    private processSortEvent(event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>, click?: boolean) {
        const clickedCell = cellFromEvent(event);

        // Check if we clicked on an actionable area; walk up to see if we clicked within one
        let testHtmlElement = event.target as HTMLElement;
        let actionableClick = click ? false : testHtmlElement.classList.contains("bolt-table-header-cell-actionable");
        if (testHtmlElement.classList.contains("bolt-table-header-sizer")) {
            let headerElement = testHtmlElement.closest(".bolt-table-header-cell-actionable") as HTMLElement;
            if (headerElement) {
                headerElement.focus();
                setFocusVisible(true);
            }
        }
        while (!actionableClick && testHtmlElement !== clickedCell.cellElement) {
            if (testHtmlElement.parentElement) {
                testHtmlElement = testHtmlElement.parentElement;
            } else {
                break;
            }
            actionableClick = testHtmlElement.classList.contains("bolt-table-header-cell-actionable");
        }

        if (clickedCell.rowIndex === -1 && actionableClick) {
            const column = this.props.columns[clickedCell.cellIndex];

            // If the column is currently sorted ascending then we need to invert the sort.
            if (column && column.sortProps) {
                this.onSort(
                    clickedCell.cellIndex,
                    column.sortProps.sortOrder === SortOrder.ascending ? SortOrder.descending : SortOrder.ascending,
                    event
                );

                const sortOrderAnnounce =
                    column.sortProps.sortOrder === SortOrder.ascending
                        ? column.sortProps.ariaLabelAscending ?? Resources.ColumnSortedAscendingAnnouncement
                        : column.sortProps.ariaLabelDescending ?? Resources.ColumnSortedDescendingAnnouncement;
                announce(sortOrderAnnounce, true);
                event.preventDefault();
            }
        }
    }
}

/**
 * sortItems is a helper method that works with the ColumnSorting and a Table
 * component to make it eaiser to maintain the props of the table. This function
 * will update the column definitions and return the sorted data. The caller
 * needs to update the props to the table appropriately after calling this
 * method.
 *
 * @param columnIndex The column that should be sorted.
 * @param sortOrder The order the data should be sorted.
 * @param sortFunctions An array of sort functions. Each sortable column should
 *  have a function supplied. If there are non-sortable columns, null should be
 *  supplied for their index.
 * @param columns The column definitions for the table.
 * @param items The array of items that should be sorted. Note: This is done in
 *  place so the input array will be updated.
 *
 * @return The resulting sorted array of items.
 */
export function sortItems<T>(
    columnIndex: number,
    sortOrder: SortOrder,
    sortFunctions: Array<((item1: T, item2: T) => number) | null>,
    columns: ITableColumn<T>[],
    items: T[]
): T[] {
    let sortFunction = sortFunctions[columnIndex];

    // If we are sorting descending, invert the sorting function.
    if (sortFunction && sortOrder === SortOrder.descending) {
        sortFunction = (item1, item2) => {
            return -(sortFunctions[columnIndex] as (item1: T, item2: T) => number)(item1, item2);
        };
    }

    // Update the sortOrder for each of the columns.
    for (let index = 0; index < columns.length; index++) {
        const column = columns[index];
        if (column.sortProps) {
            column.sortProps.sortOrder = index === columnIndex ? sortOrder : undefined;
        }
    }

    if (sortFunction) {
        return items.sort(sortFunction);
    } else {
        return items;
    }
}
